import { useTranslation } from 'react-i18next';
import { type Route } from '../../../../store/admin/routesStore';
import {
  getStatusBadgeClass,
  getStatusBadgeStyle,
  getStatusBadgeLabel,
} from '../../../../utils/getStatusEntity';

interface ThemeClasses {
  cardBg: string;
  cardBorder: string;
  text: string;
  textSecondary: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

interface RoutesCardProps {
  route: Route;
  themeClasses: ThemeClasses;
  onDetailsClick: (route: Route) => void;
  onEditClick?: (route: Route) => void;
}

const formatDuration = (minutes: number | undefined): string => {
  if (!minutes || minutes <= 0) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export default function RoutesCard({ route, themeClasses, onDetailsClick, onEditClick }: RoutesCardProps) {
  const { t } = useTranslation('adminSections');
  return (
    <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all`}>

      {/* Route Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div>
            <h3 className={`font-semibold ${themeClasses.text} max-w-[220px] mb-2`}>{route.name}</h3>
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusBadgeClass(route.status)}`}
              style={getStatusBadgeStyle(route.status)}
            >
              {getStatusBadgeLabel(route.status, t, t('units.status.unknown'))}
            </span>
          </div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
          <i className="ri-route-line text-xl text-primary-500"></i>
        </div>
      </div>

      {/* Content */}
      <div className={`flex flex-col space-y-2 pb-4 gap-4 border-b ${themeClasses.cardBorder}`}>
        <p className={`${themeClasses.textSecondary} text-sm line-clamp-3`}>{route.description}</p>

        <div className="flex flex-col space-y-2">
          <div className='flex justify-between w-full'>
            <span className={`${themeClasses.textSecondary} text-sm`}>Puntos:</span>
            <span className={`${themeClasses.text} text-sm font-medium`}>{route.principal_route?.length || 0}</span>
          </div>

          <div className='flex justify-between w-full'>
            <span className={`${themeClasses.textSecondary} text-sm`}>Distancia:</span>
            <span className={`${themeClasses.text} text-sm font-medium`}>{route.principal_route_distance?.toFixed(2) || 0} Km</span>
          </div>

          <div className='flex justify-between w-full'>
            <span className={`${themeClasses.textSecondary} text-sm`}>Duración est:</span>
            <span className={`${themeClasses.text} text-sm font-medium`}>{formatDuration(route.principal_route_duration)}</span>
          </div>

          <div className='flex justify-between w-full'>
            <span className={`${themeClasses.textSecondary} text-sm`}>Vehículos en ruta:</span>
            <span className={`${themeClasses.text} text-sm font-medium`}>{route.vehicles_count || 0}</span>
          </div>
        </div>

      </div>

      {/* Actions */}
      <div className="flex space-x-2 my-3">
        <button 
          onClick={() => onEditClick?.(route)}
          className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}
        >
          Editar
        </button>
        <button 
          onClick={() => onDetailsClick(route)}
          className="py-2 px-3 bg-primary-500 hover:bg-primary-400 text-white rounded-lg text-sm font-medium transition-colors">
          Detalles
        </button>
      </div>
    </div>
  );
}
